import React from 'react';
import { useSelector } from 'react-redux';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';

import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ContentType } from 'recharts/types/component/Tooltip';
import OutLineInfoIcon from 'src/assets/images/icons/OutLineInfoIcon';
import { IPerformance } from 'src/reducers/leaderboardSlice';
import { RootState } from 'src/store';
import { dateFormat } from 'src/utils/DateHelper';
import { formatAmount, shortNumberNotation } from 'src/utils/commonUtils';
import styles from './Leaderboard.module.scss';
import cx from 'classnames';
import { tickFormatterHandler } from 'src/utils/Chart.utils';
import {
  Directions,
  Tooltip as TooltipV2,
  TooltipContent,
  TooltipTrigger
} from '../TooltipV2/TooltipV2';

interface ICumulativeCashCollected {
  data: IPerformance;
  width?: number;
  height?: number;
  legendWrapperStyle?: React.CSSProperties;
  graphWrapperContainerClass?: string;
  isWeekData?: boolean;
}

const MIN_LEVEL = 1;
const CURRENT_AGENT_LABEL = 'You';
const DELTAS = {
  stepUp: 1.1,
  stepDown: 0.9
};
const DEFAULT_MIN = -1e10;
const DEFAULT_MAX = 1e10;
const AGENTS_LINE_COLOR = [
  '#F97474',
  '#FAB5A1',
  '#FFBC68',
  '#FFF04F',
  '#C5DB15',
  '#A9F12F',
  '#6ED02F'
];

const axisFontStyle = { fontFamily: 'Inter', fontWeight: '400', fontSize: '12px', fill: '#1c1c1c' };

const stepperFunction = (value = 0, type: 'stepUp' | 'stepDown') => {
  return value * DELTAS[type];
};

const formatYAxisValues = (value: number | unknown) => {
  const parsedValue = shortNumberNotation(value, 1);
  return '₹' + parsedValue;
};

const computeMin = (
  first: string | number | null,
  second: string | number | null,
  third: string | number | null
) => {
  return Math.min(
    first ? +first : DEFAULT_MAX,
    second ? +second : DEFAULT_MAX,
    third ? +third : DEFAULT_MAX
  );
};

const computeMax = (
  first: string | number | null,
  second: string | number | null,
  third: string | number | null
) => {
  return Math.max(
    first ? +first : DEFAULT_MIN,
    second ? +second : DEFAULT_MIN,
    third ? +third : DEFAULT_MIN
  );
};

const CumulativeCashCollected: React.FC<ICumulativeCashCollected> = props => {
  const {
    data,
    width = 350,
    height = 150,
    legendWrapperStyle,
    graphWrapperContainerClass = '',
    isWeekData = false
  } = props;

  // for computation set these variables inversely
  let min = DEFAULT_MAX;
  let max = DEFAULT_MIN;
  const agentLevelRanking = useSelector(
    (state: RootState) => state?.leaderboard?.agentLevelRanking
  );
  const maxLevel = useSelector((state: RootState) => state?.leaderboard?.maxLevel);
  const dataPoints = data?.data;
  dataPoints?.forEach(dataPoint => {
    const dataPointMin = computeMin(
      dataPoint.lowerLevelCashCollectedInLast30Days,
      dataPoint.cashCollectedInLast30Days,
      dataPoint.upperLevelCashCollectedInLast30Days
    );
    const dataPointMax = computeMax(
      dataPoint.lowerLevelCashCollectedInLast30Days,
      dataPoint.cashCollectedInLast30Days,
      dataPoint.upperLevelCashCollectedInLast30Days
    );
    if (dataPointMin < min) {
      min = dataPointMin;
    }
    if (dataPointMax > max) {
      max = dataPointMax;
    }
  });

  const handleName = (name: number | null, type: 'upper' | 'lower') => {
    if (!name) return '';
    if (type === 'upper') {
      return 'Level ' + (name + 1) + ' Agent';
    }
    if (type === 'lower') {
      return 'Level ' + (name - 1) + ' Agent';
    }
  };

  if (!dataPoints) {
    return <div className={styles.dataNotFound}>Data Not Available</div>;
  }

  return (
    <div className={cx(styles.graphWrapper, graphWrapperContainerClass)}>
      <LineChart className={styles.lineChart} width={width} height={height} data={dataPoints}>
        <defs>
          <linearGradient id="colorYou" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#5A57FF" />
            <stop offset="100%" stopColor="#FF5789" />
          </linearGradient>
        </defs>
        <CartesianGrid verticalPoints={[0]} />
        <XAxis
          ticks={data.xaxisDomainValues ?? undefined}
          tick={{ ...axisFontStyle, fontSize: '12px' }}
          dataKey="xaxis"
          tickLine={true}
          padding={'no-gap'}
          minTickGap={0}
          interval={0}
          dx={isWeekData ? -5 : -15}
          dy={5}
          tickFormatter={date => tickFormatterHandler(date, isWeekData)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          domain={[stepperFunction(min, 'stepDown'), stepperFunction(max, 'stepUp')]}
          tick={axisFontStyle}
          tickFormatter={formatYAxisValues}
        />
        <Tooltip wrapperStyle={{ outline: 'none' }} content={<CustomTooltip />} />
        <Legend
          content={RenderCustomizedLegend}
          wrapperStyle={{ ...axisFontStyle, top: -10, ...legendWrapperStyle }}
          iconSize={5}
          iconType="circle"
          layout="horizontal"
          verticalAlign="top"
          align="right"
          className={styles.legends}
        />
        {agentLevelRanking && agentLevelRanking !== MIN_LEVEL && (
          <Line
            isAnimationActive={false}
            name={handleName(agentLevelRanking, 'lower')}
            dataKey="lowerLevelCashCollectedInLast30Days"
            stroke={AGENTS_LINE_COLOR?.[agentLevelRanking - 2]}
            strokeDasharray="5 5"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 0 }}
          />
        )}
        <Line
          isAnimationActive={false}
          stroke="url(#colorYou)"
          name={CURRENT_AGENT_LABEL}
          dataKey="cashCollectedInLast30Days"
          dot={false}
          strokeWidth={2}
        />
        {agentLevelRanking && agentLevelRanking !== maxLevel && (
          <Line
            isAnimationActive={false}
            name={handleName(agentLevelRanking, 'upper')}
            dataKey="upperLevelCashCollectedInLast30Days"
            stroke={AGENTS_LINE_COLOR?.[agentLevelRanking]}
            strokeDasharray="5 5"
            dot={false}
            strokeWidth={2}
            activeDot={{ r: 0 }}
          />
        )}
      </LineChart>
    </div>
  );
};

export default CumulativeCashCollected;

const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const currentUserData = payload.filter(data => data.name === CURRENT_AGENT_LABEL);
    if (!currentUserData) return null;
    return (
      <div
        style={{
          height: 'fit-content',
          border: '#E8E8E8',
          background: '#ffffff',
          borderRadius: 4,
          padding: 4,
          boxShadow:
            ' 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 2px 1px rgba(0, 0, 0, 0.06), 0px 1px 1px rgba(0, 0, 0, 0.08)'
        }}
      >
        <p className={styles.cashCollected}>Cash Collected</p>
        <p className={styles.amount}>{formatAmount(currentUserData?.[0]?.value)}</p>
        <p className={styles.date}>{`${dateFormat(new Date(label), 'DD MMM')}`}</p>
      </div>
    );
  }
  return null;
};

const RenderCustomizedLegend: React.FC<ContentType<ValueType, NameType>> = props => {
  const { payload } = props;
  return (
    <div className={styles.cashCollectedLegend}>
      {payload.map(entry => {
        const { color } = entry;
        const style = { backgroundColor: color, height: 3, width: 10, marginRight: 4 };
        if (entry.value === 'You') {
          return (
            <div key={color} className={styles.legend}>
              <div className={styles.you} />
              <span className={styles.entryTooltipWrapper}>
                {entry.value}
                <TooltipV2 placement={Directions.BOTTOM}>
                  <TooltipTrigger className={styles.cashCollectedTooltipTriggerWrapper}>
                    <OutLineInfoIcon />
                  </TooltipTrigger>
                  <TooltipContent
                    className={cx(styles.tooltipWrapper, styles.cashCollectedTooltipWrapper)}
                    arrowColor="var(--bg-primary)"
                  >
                    <YouToolTip />
                  </TooltipContent>
                </TooltipV2>
              </span>
            </div>
          );
        }
        return (
          <div key={color} className={styles.legend}>
            <div style={style} /> <span>{entry.value}</span>
          </div>
        );
      })}
    </div>
  );
};

const YouToolTip = () => {
  return (
    <div key={'key'} className={styles.description}>
      <div className={styles.legendContainer}>
        <div className={styles.goingDown} />
        <span>Going Down</span>
      </div>
      <div className={styles.legendContainer}>
        <div className={styles.goingUp} />
        <span>Going Up</span>
      </div>
    </div>
  );
};
